import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock } from "lucide-react";
import { Instagram, Facebook } from "lucide-react";
import { API } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { WhatsAppIcon } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const TOPICS = ["Order Inquiry", "Sizing Help", "Wholesale", "Press", "Custom Fit"];

const FAQS = [
  ["How long does delivery take?", "Orders dispatch within 24-48 hours. Metro cities usually receive in 2-4 days, the rest of India in 4-7 days. Express delivery (2-3 days) is available at checkout."],
  ["What is your exchange policy?", "Returns are not accepted. Exchange is accepted only for manufacturing defects or the wrong size received — with an uncut, unedited, continuous parcel-opening video, registered within 48 hours of delivery on WhatsApp or email."],
  ["Can I get a custom fit?", "Yes — for a small tailoring fee, most sets can be adjusted to your measurements. Choose 'Custom Fit' in the form and we'll take it from there."],
  ["How do I pay for my order?", "We accept UPI, credit & debit cards, NetBanking and wallets — all processed securely through Razorpay at checkout. Prepaid orders get priority dispatch."],
];

export default function Contact() {
  const { config } = useStore();
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);
  const wa = config.whatsapp_number || "919960705886";

  useSEO({
    title: "Contact Us — Just Kords",
    description: "Questions about your order, sizing, wholesale or custom fits? Reach the Just Kords team on WhatsApp, email or the contact form.",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we usually reply within a few hours.");
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Couldn't send right now. Please try again.");
    }
    setSending(false);
  };

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <Reveal className="border-b border-linec pb-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Say Hello</p>
          <h1 className="mt-4 font-serif text-5xl font-light sm:text-6xl">Contact Us</h1>
        </Reveal>

        <div className="grid gap-14 py-14 lg:grid-cols-2">
          <Reveal>
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mutedc">Name</span>
                  <input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full border border-linec bg-white px-4 py-3 text-sm focus:border-crimson focus:outline-none" placeholder="Your name" />
                </label>
                <label className="block">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mutedc">Email</span>
                  <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full border border-linec bg-white px-4 py-3 text-sm focus:border-crimson focus:outline-none" placeholder="you@email.com" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mutedc">WhatsApp number (optional — so we can reply there)</span>
                  <input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full border border-linec bg-white px-4 py-3 text-sm focus:border-crimson focus:outline-none" placeholder="+91 98765 43210" />
                </label>
              </div>
              <label className="block">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mutedc">Topic</span>
                <select data-testid="contact-topic-select" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="mt-2 w-full border border-linec bg-white px-4 py-3 text-sm focus:border-crimson focus:outline-none">
                  {TOPICS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mutedc">Message</span>
                <textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full border border-linec bg-white px-4 py-3 text-sm focus:border-crimson focus:outline-none" placeholder="How can we help?" />
              </label>
              <button data-testid="contact-submit" disabled={sending} type="submit"
                className="rounded-full bg-crimson px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark disabled:opacity-60">
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>

            <div className="mt-12" data-testid="contact-faq">
              <h3 className="font-serif text-2xl">Frequently asked</h3>
              <Accordion type="single" collapsible className="mt-4">
                {FAQS.map(([q, a]) => (
                  <AccordionItem key={q} value={q}>
                    <AccordionTrigger data-testid={`contact-faq-${q.split(" ")[0].toLowerCase()}`} className="text-left text-sm font-semibold">{q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-mutedc">{a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-4">
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card"
                className="flex items-center gap-5 border border-linec bg-white p-6 transition-colors duration-200 hover:border-crimson">
                <span className="rounded-full bg-crimson-sub p-3.5 text-crimson"><WhatsAppIcon size={20} /></span>
                <div>
                  <p className="font-semibold">WhatsApp us</p>
                  <p className="text-sm text-mutedc">{`+91 ${wa.slice(2, 7)} ${wa.slice(7)}`} — fastest replies</p>
                </div>
              </a>
              <div className="flex items-center gap-5 border border-linec bg-white p-6" data-testid="contact-email-card">
                <span className="rounded-full bg-crimson-sub p-3.5 text-crimson"><Mail size={20} /></span>
                <div>
                  <p className="font-semibold">{config.email}</p>
                  <p className="text-sm text-mutedc">For orders, returns & everything else</p>
                </div>
              </div>
              <div className="flex items-center gap-5 border border-linec bg-white p-6" data-testid="contact-hours-card">
                <span className="rounded-full bg-crimson-sub p-3.5 text-crimson"><Clock size={20} /></span>
                <div>
                  <p className="font-semibold">Mon – Sat, 10am – 7pm IST</p>
                  <p className="text-sm text-mutedc">We reply within a few hours</p>
                </div>
              </div>
              <div className="flex items-center gap-5 border border-linec bg-white p-6" data-testid="contact-studio-card">
                <span className="rounded-full bg-crimson-sub p-3.5 text-crimson"><MapPin size={20} /></span>
                <div>
                  <p className="font-semibold">Studio (visits by appointment)</p>
                  <p className="text-sm text-mutedc">Mumbai, India</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <a href={config.socials?.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-card"
                  className="flex items-center gap-3 border border-linec bg-white p-5 transition-colors duration-200 hover:border-crimson">
                  <Instagram size={18} className="text-crimson" /> <span className="text-sm font-semibold">@justkords</span>
                </a>
                <a href={config.socials?.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-card"
                  className="flex items-center gap-3 border border-linec bg-white p-5 transition-colors duration-200 hover:border-crimson">
                  <Facebook size={18} className="text-crimson" /> <span className="text-sm font-semibold">Just Kords</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
